import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSignOutAlt,
  faHome, faUser, faMessage, faLink, faRobot,
  faExclamationTriangle,
  faChartBar,
  faNewspaper,
  faUsersCog,
  faCreditCard, faRss,
  faLineChart,
  faMedal,
  faThLarge,
  faEdit,
  faChevronDown,
  faCog,
  faQuestionCircle,
  faChevronLeft,
  faChevronRight,
  faFolderOpen,
  faChartPie,
  faLifeRing,
  faPlus,
  faBars,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import Select from 'react-select';
import './sidestyles.css';
import logo from "../../assets/Knack Vibes (2).png";
import defaultProfile from "../../assets/default-image.png";
import Swal from 'sweetalert2';
import WelcomeBanner from './WelcomeBanner';
import { validateProfileComplete } from '../../utils/profileValidation';

const SideNavbar = ({ children, headerRight }) => {
  // Load collapsed state from localStorage, default to false if not set
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  // Mobile sidebar state (separate from collapsed state)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState('');
  const [engagementToolsOpen, setEngagementToolsOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [supportAccountOpen, setSupportAccountOpen] = useState(false);
  const [hoveredDropdown, setHoveredDropdown] = useState(null); // Track which dropdown is hovered when collapsed
  const hoverTimeoutRef = React.useRef(null); // Ref for hover timeout
  const navigate = useNavigate();
  const location = useLocation();
  const RoleID = localStorage.getItem("RoleID");
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(localStorage.getItem('UserID') || '');
  const [profileImageUrl, setProfileImageUrl] = useState(localStorage.getItem('ProfileUrl') || defaultProfile);
  const [selectedUserName, setSelectedUserName] = useState(localStorage.getItem('FullName') || ''); // New state for full name
  const [userProfile, setUserProfile] = useState(() => {
    const storedProfile = localStorage.getItem('userProfile');
    if (storedProfile) {
      try {
        return JSON.parse(storedProfile);
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const ClientID = localStorage.getItem('ClientID')
  const isActive = localStorage.getItem('IsActive'); // Should be "0" or "1"
  const UserID = localStorage.getItem('UserID');
  const loginType = localStorage.getItem("AccountType");
  const Token = localStorage.getItem('AccessToken');
  const [hasSubscriptionOrTrial, setHasSubscriptionOrTrial] = useState(true); // Default to true to avoid flickering

  // Function to determine active state based on current URL
  const getActiveState = (path) => {
    return location.pathname === path;
  };

  // Function to determine if dropdown should be open based on current URL
  const shouldDropdownBeOpen = (dropdownPaths) => {
    return dropdownPaths.some(path => location.pathname.startsWith(path));
  };

  // Sync user name from localStorage immediately on mount and navigation
  useEffect(() => {
    // Always sync from localStorage to ensure immediate display
    const fullName = localStorage.getItem('FullName');
    if (fullName) {
      setSelectedUserName(fullName);
    }
    const profileUrl = localStorage.getItem('ProfileUrl');
    if (profileUrl) {
      setProfileImageUrl(profileUrl || defaultProfile);
    }
    
    // Check if we're returning from LinkedIn login and refresh profile
    const urlParams = new URLSearchParams(window.location.search);
    const linkedinCallback = urlParams.get('linkedin_callback') || urlParams.get('code') || urlParams.get('accessToken') || urlParams.get('tokenID');
    if (linkedinCallback && UserID) {
      // Refresh profile after LinkedIn login - wait a bit for backend to process
      setTimeout(() => {
        getprofiledetails();
      }, 1500);
    }
  }, [location.pathname, UserID]);

  // Update active states and dropdown states based on current URL
  useEffect(() => {
    const currentPath = location.pathname;

    // Set selected item based on current path
    if (currentPath === '/home' || currentPath === '/Pagehome' || currentPath === '/adminhome') {
      setSelectedItem('home');
    } else if (currentPath === '/user-form') {
      setSelectedItem('user-form');
    } else if (currentPath === '/create-post') {
      setSelectedItem('create-post');
    } else if (currentPath === '/schedule-list' || currentPath === '/KnackFeeds' || currentPath === '/KnackChallenges' || currentPath === '/KnackMoments') {
      setSelectedItem('engagement-tools');
    } else if (currentPath === '/feature-limits' || currentPath === '/viewpostsperformance') {
      setSelectedItem('analytics');
    } else if (currentPath === '/subscription' || currentPath === '/resources') {
      setSelectedItem('support-account');
    }

    // Set dropdown states based on current path
    setEngagementToolsOpen(shouldDropdownBeOpen(['/schedule-list', '/KnackFeeds', '/KnackChallenges', '/KnackMoments']));
    setAnalyticsOpen(shouldDropdownBeOpen(['/feature-limits', '/viewpostsperformance']));
    setSupportAccountOpen(shouldDropdownBeOpen(['/subscription', '/resources']));
  }, [location.pathname]);

  const getprofiledetails = () => {
    if (!UserID) return;
    
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/get-profile-info`, {
      params: { UserID }
    })
      .then((response) => {
        console.log("API Response:", response.data); // Log the API response

        const profiles = response.data;
        if (profiles && profiles.length > 0) {
          const profile = profiles[0];
          // Update all localStorage values
          localStorage.setItem('AccessToken', profile?.AccessToken || '');
          localStorage.setItem('Tokenid', profile?.Tokenid || '');
          localStorage.setItem('FullName', profile?.FullName || '');
          localStorage.setItem('Email', profile?.Email || '');
          localStorage.setItem('ProfileUrl', profile?.ProfileUrl || defaultProfile);
          localStorage.setItem('userProfile', JSON.stringify(profiles));
          
          // Update state immediately
          setSelectedUserName(profile?.FullName || '');
          setProfileImageUrl(profile?.ProfileUrl || defaultProfile);
          setUserProfile(profiles);
        } else {
          console.error('No matching profile found');
        }
      })
      .catch((error) => {
        console.error('Error fetching profile details:', error);
      });
  }

  // Refresh profile when AccessToken changes (after LinkedIn login)
  useEffect(() => {
    if (!UserID) return;
    
    const currentToken = localStorage.getItem('AccessToken');
    const lastToken = sessionStorage.getItem('lastAccessToken');
    
    // If token changed, refresh profile
    if (currentToken && currentToken !== lastToken && currentToken !== '') {
      sessionStorage.setItem('lastAccessToken', currentToken);
      // Small delay to ensure backend has processed the new token
      setTimeout(() => {
        getprofiledetails();
      }, 500);
    } else if (!lastToken && currentToken && currentToken !== '') {
      // First load, store the token and refresh profile
      sessionStorage.setItem('lastAccessToken', currentToken);
      // Refresh profile on initial load to ensure we have latest data
      getprofiledetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Token, UserID]);

  // Check if user has subscription or free trial
  useEffect(() => {
    const checkSubscriptionOrTrial = async () => {
      try {
        const subscriptionEndDate = localStorage.getItem('subscriptionEndDate');
        const subscriptionType = localStorage.getItem('SubscriptionType');
        const createdDateStr = localStorage.getItem('CreatedDate');
        
        // Check if user has active subscription
        const hasActiveSubscription = subscriptionType && subscriptionType !== 'null' && 
                                      subscriptionEndDate && subscriptionEndDate !== 'null';
        
        if (hasActiveSubscription) {
          // Check if subscription hasn't expired
          const endDate = new Date(subscriptionEndDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);
          
          if (endDate >= today) {
            setHasSubscriptionOrTrial(true);
            return;
          }
        }
        
        // If no active subscription, check for free trial
        if (createdDateStr) {
          try {
            // Fetch user profile to get TrialExtensionDays
            
            
            let trialExtensionDays = 0;
            trialExtensionDays = localStorage.getItem('userProfile[0].TrialExtensionDays') || 0;
            
            
            const createdDate = new Date(createdDateStr);
            createdDate.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            const trialDaysUsed = Math.floor((today - createdDate) / (1000 * 60 * 60 * 24));
            const totalTrialDays = 30 + trialExtensionDays;
            const trialDaysRemaining = totalTrialDays - trialDaysUsed;
            
            // User has active trial if trial days remaining > 0
            setHasSubscriptionOrTrial(trialDaysRemaining > 0);
          } catch (error) {
            console.error('Error checking trial status:', error);
            // Fallback: assume trial is active if we can't check
            setHasSubscriptionOrTrial(true);
          }
        } else {
          // No created date, assume no trial
          setHasSubscriptionOrTrial(false);
        }
      } catch (error) {
        console.error('Error checking subscription/trial status:', error);
        // On error, default to showing items to avoid blocking users
        setHasSubscriptionOrTrial(true);
      }
    };

    if (UserID) {
      checkSubscriptionOrTrial();
    }
  }, [UserID, location.pathname]);

  useEffect(() => {
    if (ClientID == 'null') return;
    const fetchUsers = async () => {
      try {

        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getallusers`, {
          params: {
            ClientID: ClientID,
            LoggedInUserID: selectedUser,  // Pass the logged-in user's ID
            Type: "Users"
          },
        });
        setUsers(response?.data);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();

    // Retrieve selected user information from local storage
    const storedUserId = localStorage.getItem('UserID');
    if (storedUserId) {
      setSelectedUser(storedUserId);
      setSelectedUserName(localStorage.getItem('FullName') || ''); // Update the selected user name
    }

  }, []);

  useEffect(() => {
    if (RoleID === 1 || RoleID === 4) {
      checkLastLoginExpiry();
    }
  }, [])

  const checkUserLoginExpiry = (storedLoginTime) => {
    if (!storedLoginTime || storedLoginTime === "null") {
      return 'expired'; // Properly return 'expired'
    }

    const lastLoginDate = new Date(storedLoginTime);
    const now = new Date();
    const diffInMs = now - lastLoginDate;
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24); // convert ms to days

    return diffInDays > 60 ? 'expired' : 'valid'; // 60 days threshold (LinkedIn token expiration)
  };




  const handleChange = async (selectedOption) => {
    const userId = selectedOption.value;
    setSelectedUser(userId);

    if (userId) {
      try {
        // Fetch social media account details
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/GetSocialMediaAccountDet/${userId}`);
        const { AccessToken, TokenID, AccountId, AccountType, ProfileUrl, FullName, Email, LastLoginTime } = response.data;
        const expiryStatus = checkUserLoginExpiry(LastLoginTime);

        if (expiryStatus === 'expired') {
          await Swal.fire({
            icon: 'warning',
            title: 'Token Expired',
            text: `${FullName}'s LinkedIn session has expired. They may need to re-authenticate with LinkedIn.`,
            timer: 5000, // ⏳ Show for 5 seconds
            timerProgressBar: true,
            showConfirmButton: true,
            confirmButtonText: 'OK',
            allowOutsideClick: true,
            allowEscapeKey: true,
          });
        }
        // Store user information in local storage
        localStorage.setItem('AccessToken', AccessToken);
        localStorage.setItem('Tokenid', TokenID);
        localStorage.setItem('AccountType', AccountType);
        localStorage.setItem('SelectedUserAccountId', AccountId);
        localStorage.setItem('UserID', userId);
        localStorage.setItem('ProfileUrl', ProfileUrl || defaultProfile);
        localStorage.setItem('FullName', FullName); // Store Full Name
        localStorage.setItem('Email', Email);
        localStorage.setItem('lastLoginTime', LastLoginTime || ''); // optional if returned
        
        // ✅ Also fetch and update full profile information
        try {
          const profileResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/get-profile-info`, {
            params: { UserID: userId }
          });
          
          if (profileResponse.data && profileResponse.data.length > 0) {
            const profile = profileResponse.data[0];
            // Update additional profile fields
            localStorage.setItem('userProfile', JSON.stringify(profileResponse.data));
            setUserProfile(profileResponse.data);
            
            // Update any additional fields that might be in the profile
            if (profile.OrganizationName) {
              localStorage.setItem('OrganizationName', profile.OrganizationName);
            }
            if (profile.CountryCode) {
              localStorage.setItem('CountryCode', profile.CountryCode);
            }
          }
        } catch (profileError) {
          console.warn('Error fetching full profile, using social media account data:', profileError);
        }
        
        console.log("✅ Updated localStorage for user:", userId, "FullName:", FullName);
        
        // ✅ Update local state IMMEDIATELY (before reload)
        setProfileImageUrl(ProfileUrl || defaultProfile);
        setSelectedUserName(FullName); // Update selected user name
        window.location.reload();
      } catch (error) {
        console.error('Error fetching social media account:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load user details. Please try again.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192'
        });
      }
    }
  };

  const userOptions = users?.map(user => ({
    value: user.UserID,
    label: user.UserName
  }));

  const selectedOption = userOptions?.find(user => user.value === selectedUser) || null;

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    // Persist the collapsed state to localStorage
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
  };

  // Toggle mobile sidebar
  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  // Close mobile sidebar
  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };

  // Close mobile sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileSidebarOpen) {
        const sidebar = document.getElementById('sidebar');
        const hamburgerButton = document.getElementById('mobile-hamburger-btn');
        if (sidebar && !sidebar.contains(event.target) && 
            hamburgerButton && !hamburgerButton.contains(event.target)) {
          closeMobileSidebar();
        }
      }
    };

    if (isMobileSidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when sidebar is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMobileSidebarOpen]);

  // Close mobile sidebar when route changes
  useEffect(() => {
    closeMobileSidebar();
  }, [location.pathname]);

  const handleLogout = () => {
    // Clear all localStorage data for all users
    localStorage.clear();
    // Navigate to login page
    navigate("/Login");
  };

  const handleLinkedInLogin = () => {
    if (!loginType) {
      Swal.fire({
        icon: 'warning',
        title: 'Account Type Required',
        text: 'Please select an account type before login with LinkedIn'
      });
      return;
    }
    const linkedinAuthorizeUrl = `${process.env.REACT_APP_API_BASE_URL}/api/linkedin/authorize?UserID=${UserID}&loginType=${loginType}`;
    window.location.href = linkedinAuthorizeUrl;
    const now = new Date().toISOString();
    localStorage.setItem('lastLoginTime', now);
    getprofiledetails();
  };

  const checkLastLoginExpiry = () => {
    const storedLoginTime = localStorage.getItem('lastLoginTime');
    console.log("63", typeof storedLoginTime, storedLoginTime)
    if (storedLoginTime == null || storedLoginTime == "null") {
      Swal.fire({
        title: 'Session Expired',
        text: 'Redirecting to LinkedIn login...',
        icon: 'info',
        timer: 10000,
        showConfirmButton: false,
        allowOutsideClick: false,
        didClose: () => {
          handleLinkedInLogin();
        }
      });
      return;
    }
    const lastLoginDate = new Date(storedLoginTime);
    const now = new Date();

    const diffInMs = now - lastLoginDate;
    const diffInMins = diffInMs / (1000 * 60 * 60 * 24);

    if (diffInMins > 60) {
      Swal.fire({
        title: 'Session Expired',
        text: 'Redirecting to LinkedIn login...',
        icon: 'info',
        timer: 10000,
        showConfirmButton: false,
        allowOutsideClick: false,
        didClose: () => {
          handleLinkedInLogin();
        }
      });
    }
  };

  const handleNavigate = (path, item) => {
    // Check profile completion before navigating to features that require profile
    const profileRequiredRoutes = ['/create-post', '/KnackFeeds', '/KnackChallenges', '/KnackMoments', '/schedule'];
    if (profileRequiredRoutes.includes(path)) {
      if (!validateProfileComplete({ navigate })) {
        // Profile is incomplete - validation shows alert and navigates to profile hub if user confirms
        return; // Don't navigate if profile is incomplete
      }
    }
    
    setSelectedItem(item);
    navigate(path);
    // Close mobile sidebar when navigating
    closeMobileSidebar();
  };

  // Cleanup timeout on unmount or when sidebar state changes
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [isCollapsed]);

  // Global back button handler: navigate to previous page when back icon is clicked
  useEffect(() => {
    const handleGlobalBackClick = (e) => {
      const target = e.target;
      if (!target) return;
      // matches <i class="fa fa-arrow-left ..."> or similar
      const isBackIcon = target.classList && (target.classList.contains('fa-arrow-left') || (target.classList.contains('fa') && target.classList.contains('fa-arrow-left')));
      if (isBackIcon) {
        e.preventDefault();
        // Visual cue for pointer
        target.style.cursor = 'pointer';
        // Go back in history
        window.history.back();
      }
    };
    document.addEventListener('click', handleGlobalBackClick, true);
    return () => document.removeEventListener('click', handleGlobalBackClick, true);
  }, []);

  return (
    <div className={`wrapper ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Mobile Menu Button */}
      <button
        id="mobile-hamburger-btn"
        className={`mobile-hamburger-btn ${isMobileSidebarOpen ? 'sidebar-open' : ''}`}
        onClick={toggleMobileSidebar}
        aria-label="Toggle navigation menu"
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </button>

      {/* Mobile Overlay */}
      {isMobileSidebarOpen && (
        <div 
          className="mobile-sidebar-overlay"
          onClick={closeMobileSidebar}
          aria-hidden="true"
        />
      )}

      <nav 
        id="sidebar" 
        className={`${isCollapsed ? 'collapsed' : ''} ${isMobileSidebarOpen ? 'mobile-active' : ''}`}
      >
        <div className="sidebar-header" style={{ backgroundColor: '#2e3192', padding: '15px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '100px', maxHeight: '100px', overflow: 'hidden' }}>
          {!isCollapsed && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
              <img src={logo} style={{ height: '90px', width: 'auto', maxHeight: '90px', objectFit: 'contain' }} alt="Logo" />
            </div>
          )}
          {isCollapsed && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              <img src={logo} style={{ height: '40px', width: '40px', objectFit: 'contain' }} alt="Logo" />
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className="sidebar-toggle-btn"
            style={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: '#fff',
              borderRadius: '8px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              marginLeft: isCollapsed ? '0' : '10px',
              marginTop: isCollapsed ? '-8px' : '0',
              padding: isCollapsed ? '4px' : '0',
              boxSizing: 'border-box'
            }}
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronLeft} style={{ fontSize: '14px', width: '14px', height: '14px' }} />
          </button>
        </div>

        <ul className="list-unstyled components">

          {/* // Show full SideNavbar when IsActive === true */}
          {/* Dashboard - Only show if user has subscription or free trial */}
          {hasSubscriptionOrTrial && (
            <>
              {RoleID == 1 ? (
                <li className={`dropdown ${getActiveState('/home') ? 'active' : ''}`}>
                  <a
                    href="#"
                    onClick={() => handleNavigate('/home', 'home')}
                    style={{ textDecoration: "none", position: 'relative' }}
                    title={isCollapsed ? 'Dashboard' : ''}
                  >
                    <FontAwesomeIcon icon={faHome} className='fa-icon' style={{ minWidth: '20px' }} />
                    {!isCollapsed && <span style={{ marginLeft: '8px' }}>Dashboard</span>}
                    {isCollapsed && <span className="nav-tooltip">Dashboard</span>}
                  </a>
                </li>
              ) : RoleID == 4 ? (
                <li className={`dropdown ${getActiveState('/Pagehome') ? 'active' : ''}`}>
                  <a
                    href="#"
                    onClick={() => handleNavigate('/Pagehome', 'home')}
                    style={{ textDecoration: "none", position: 'relative' }}
                    title={isCollapsed ? 'Dashboard' : ''}
                  >
                    <FontAwesomeIcon icon={faHome} className='fa-icon' style={{ minWidth: '20px' }} />
                    {!isCollapsed && <span style={{ marginLeft: '8px' }}>Dashboard</span>}
                    {isCollapsed && <span className="nav-tooltip">Dashboard</span>}
                  </a>
                </li>
              ) : (
                <li className={`dropdown ${getActiveState('/adminhome') ? 'active' : ''}`}>
                  <a
                    href="#"
                    onClick={() => handleNavigate('/adminhome', 'home')}
                    style={{ textDecoration: "none", position: 'relative' }}
                    title={isCollapsed ? 'Dashboard' : ''}
                  >
                    <FontAwesomeIcon icon={faHome} className='fa-icon' style={{ minWidth: '20px' }} />
                    {!isCollapsed && <span style={{ marginLeft: '8px' }}>Dashboard</span>}
                    {isCollapsed && <span className="nav-tooltip">Dashboard</span>}
                  </a>
                </li>
              )}
            </>
          )}


          {isActive == "true" && (<>
            <li className={`dropdown ${getActiveState('/user-form') ? 'active' : ''}`}>
              <a
                href="#"
                onClick={() => handleNavigate('/user-form', 'user-form')}
                style={{ textDecoration: "none", position: 'relative' }}
                title={isCollapsed ? 'Profile Hub' : ''}
              >
                <FontAwesomeIcon icon={faUser} className='fa-icon' style={{ minWidth: '20px' }} />
                {!isCollapsed && <span style={{ marginLeft: '8px' }}>Profile Hub</span>}
                {isCollapsed && <span className="nav-tooltip">Profile Hub</span>}
              </a>
            </li>

            <li className={`dropdown ${getActiveState('/create-post') ? 'active' : ''}`}>
              <a
                href="#"
                onClick={() => handleNavigate('/create-post', 'create-post')}
                style={{ textDecoration: "none", position: 'relative' }}
                title={isCollapsed ? 'Create Post' : ''}
              >
                <FontAwesomeIcon icon={faEdit} className='fa-icon' style={{ minWidth: '20px' }} />
                {!isCollapsed && <span style={{ marginLeft: '8px' }}>Create Post</span>}
                {isCollapsed && <span className="nav-tooltip">Create Post</span>}
              </a>
            </li>

            {/* Content Studio Dropdown */}
            <li
              className={`dropdown ${engagementToolsOpen ? 'active' : ''}`}
              onMouseEnter={() => {
                if (isCollapsed) {
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                  }
                  setHoveredDropdown('engagement');
                }
              }}
              onMouseLeave={(e) => {
                if (isCollapsed) {
                  // Clear any existing timeout
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                  }
                  // Check if moving to the flyout menu
                  const menu = e.currentTarget.querySelector('.collapsed-dropdown-menu');
                  const relatedTarget = e.relatedTarget;
                  // Only close if not moving to the flyout menu and relatedTarget is a valid Node
                  if (!menu || !relatedTarget || !(relatedTarget instanceof Node) || !menu.contains(relatedTarget)) {
                    // Add a small delay to make it easier to move to the menu
                    hoverTimeoutRef.current = setTimeout(() => {
                      setHoveredDropdown(null);
                    }, 200);
                  }
                }
              }}
              style={{ position: 'relative' }}
            >
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  // On mobile, always toggle dropdown
                  if (window.innerWidth <= 768) {
                    setEngagementToolsOpen(!engagementToolsOpen);
                  } else if (isCollapsed) {
                    // Don't navigate, let hover show the menu
                    return;
                  } else {
                    setEngagementToolsOpen(!engagementToolsOpen);
                  }
                }}
                style={{ textDecoration: "none", position: 'relative' }}
                title={isCollapsed ? 'Content Studio' : ''}
              >
                <FontAwesomeIcon icon={faFolderOpen} className='fa-icon' style={{ minWidth: '20px' }} />
                {!isCollapsed && <span style={{ marginLeft: '8px' }}>Content Studio</span>}
                {!isCollapsed && (
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    className={`fa-icon ${engagementToolsOpen ? 'rotate-chevron' : ''}`}
                    style={{ float: 'right', fontSize: '12px' }} 
                  />
                )}
                {isCollapsed && hoveredDropdown !== 'engagement' && <span className="nav-tooltip">Content Studio</span>}
              </a>
              {/* Flyout menu for collapsed state */}
              {isCollapsed && hoveredDropdown === 'engagement' && (
                <div
                  className="collapsed-dropdown-menu"
                  onMouseEnter={() => {
                    if (hoverTimeoutRef.current) {
                      clearTimeout(hoverTimeoutRef.current);
                    }
                    setHoveredDropdown('engagement');
                  }}
                  onMouseLeave={() => {
                    if (hoverTimeoutRef.current) {
                      clearTimeout(hoverTimeoutRef.current);
                    }
                    hoverTimeoutRef.current = setTimeout(() => {
                      setHoveredDropdown(null);
                    }, 200);
                  }}
                  style={{
                    position: 'absolute',
                    left: '100%',
                    top: 0,
                    backgroundColor: '#ffffff',
                    border: '1px solid #e1e5e9',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    minWidth: '220px',
                    zIndex: 10000,
                    marginLeft: '10px',
                    padding: '4px 0'
                  }}>
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNavigate('/schedule-list', 'schedule-list');
                      setHoveredDropdown(null);
                    }}
                    style={{
                      padding: '12px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#333',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: getActiveState('/schedule-list') ? '#f0f4ff' : 'transparent',
                      borderLeft: getActiveState('/schedule-list') ? '3px solid #2e3192' : '3px solid transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!getActiveState('/schedule-list')) {
                        e.currentTarget.style.backgroundColor = '#f0f4ff';
                        e.currentTarget.style.borderLeft = '3px solid #2e3192';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!getActiveState('/schedule-list')) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderLeft = '3px solid transparent';
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faMessage} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                    <span>Recurring Schedule</span>
                  </div>
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNavigate('/KnackFeeds', '/KnackFeeds');
                      setHoveredDropdown(null);
                    }}
                    style={{
                      padding: '12px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#333',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: getActiveState('/KnackFeeds') ? '#f0f4ff' : 'transparent',
                      borderLeft: getActiveState('/KnackFeeds') ? '3px solid #2e3192' : '3px solid transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!getActiveState('/KnackFeeds')) {
                        e.currentTarget.style.backgroundColor = '#f0f4ff';
                        e.currentTarget.style.borderLeft = '3px solid #2e3192';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!getActiveState('/KnackFeeds')) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderLeft = '3px solid transparent';
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faRss} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                    <span>Feeds</span>
                  </div>
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNavigate('/KnackChallenges', '/KnackChallenges');
                      setHoveredDropdown(null);
                    }}
                    style={{
                      padding: '12px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#333',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: getActiveState('/KnackChallenges') ? '#f0f4ff' : 'transparent',
                      borderLeft: getActiveState('/KnackChallenges') ? '3px solid #2e3192' : '3px solid transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!getActiveState('/KnackChallenges')) {
                        e.currentTarget.style.backgroundColor = '#f0f4ff';
                        e.currentTarget.style.borderLeft = '3px solid #2e3192';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!getActiveState('/KnackChallenges')) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderLeft = '3px solid transparent';
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faMedal} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                    <span>Challenges</span>
                  </div>
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNavigate('/KnackMoments', 'KnackMoments');
                      setHoveredDropdown(null);
                    }}
                    style={{
                      padding: '12px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#333',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: getActiveState('/KnackMoments') ? '#f0f4ff' : 'transparent',
                      borderLeft: getActiveState('/KnackMoments') ? '3px solid #2e3192' : '3px solid transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!getActiveState('/KnackMoments')) {
                        e.currentTarget.style.backgroundColor = '#f0f4ff';
                        e.currentTarget.style.borderLeft = '3px solid #2e3192';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!getActiveState('/KnackMoments')) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderLeft = '3px solid transparent';
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                    <span>Moments</span>
                  </div>
                </div>
              )}
              {engagementToolsOpen && !isCollapsed && (
                <div style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  margin: '8px 12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden'
                }}>
                  <div>
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleNavigate('/schedule-list', 'schedule-list');
                        setEngagementToolsOpen(true);
                      }}
                      style={{
                        padding: '12px 20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#333',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        backgroundColor: getActiveState('/schedule-list') ? '#f0f4ff' : 'transparent',
                        borderLeft: getActiveState('/schedule-list') ? '3px solid #2e3192' : '3px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (!getActiveState('/schedule-list')) {
                          e.currentTarget.style.backgroundColor = '#f0f4ff';
                          e.currentTarget.style.borderLeft = '3px solid #2e3192';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!getActiveState('/schedule-list')) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderLeft = '3px solid transparent';
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faMessage} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                      <span>Recurring Schedule</span>
                    </div>
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleNavigate('/KnackFeeds', '/KnackFeeds');
                        setEngagementToolsOpen(true);
                      }}
                      style={{
                        padding: '12px 20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#333',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        backgroundColor: getActiveState('/KnackFeeds') ? '#f0f4ff' : 'transparent',
                        borderLeft: getActiveState('/KnackFeeds') ? '3px solid #2e3192' : '3px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (!getActiveState('/KnackFeeds')) {
                          e.currentTarget.style.backgroundColor = '#f0f4ff';
                          e.currentTarget.style.borderLeft = '3px solid #2e3192';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!getActiveState('/KnackFeeds')) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderLeft = '3px solid transparent';
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faRss} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                      <span>Feeds</span>
                    </div>
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleNavigate('/KnackChallenges', '/KnackChallenges');
                        setEngagementToolsOpen(true);
                      }}
                      style={{
                        padding: '12px 20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#333',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        backgroundColor: getActiveState('/KnackChallenges') ? '#f0f4ff' : 'transparent',
                        borderLeft: getActiveState('/KnackChallenges') ? '3px solid #2e3192' : '3px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (!getActiveState('/KnackChallenges')) {
                          e.currentTarget.style.backgroundColor = '#f0f4ff';
                          e.currentTarget.style.borderLeft = '3px solid #2e3192';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!getActiveState('/KnackChallenges')) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderLeft = '3px solid transparent';
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faMedal} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                      <span>Challenges</span>
                    </div>
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleNavigate('/KnackMoments', 'KnackMoments');
                        setEngagementToolsOpen(true);
                      }}
                      style={{
                        padding: '12px 20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#333',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        backgroundColor: getActiveState('/KnackMoments') ? '#f0f4ff' : 'transparent',
                        borderLeft: getActiveState('/KnackMoments') ? '3px solid #2e3192' : '3px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (!getActiveState('/KnackMoments')) {
                          e.currentTarget.style.backgroundColor = '#f0f4ff';
                          e.currentTarget.style.borderLeft = '3px solid #2e3192';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!getActiveState('/KnackMoments')) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderLeft = '3px solid transparent';
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faEdit} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                      <span>Moments</span>
                    </div>
                  </div>
                </div>
              )}
            </li>

            {/* Analytics Dropdown */}
            <li
              className={`dropdown ${analyticsOpen ? 'active' : ''}`}
              onMouseEnter={() => {
                if (isCollapsed) {
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                  }
                  setHoveredDropdown('analytics');
                }
              }}
              onMouseLeave={(e) => {
                if (isCollapsed) {
                  // Clear any existing timeout
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                  }
                  // Check if moving to the flyout menu
                  const menu = e.currentTarget.querySelector('.collapsed-dropdown-menu');
                  const relatedTarget = e.relatedTarget;
                  // Only close if not moving to the flyout menu and relatedTarget is a valid Node
                  if (!menu || !relatedTarget || !(relatedTarget instanceof Node) || !menu.contains(relatedTarget)) {
                    // Add a small delay to make it easier to move to the menu
                    hoverTimeoutRef.current = setTimeout(() => {
                      setHoveredDropdown(null);
                    }, 200);
                  }
                }
              }}
              style={{ position: 'relative' }}
            >
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  // On mobile, always toggle dropdown
                  if (window.innerWidth <= 768) {
                    setAnalyticsOpen(!analyticsOpen);
                  } else if (isCollapsed) {
                    // Don't navigate, let hover show the menu
                    return;
                  } else {
                    setAnalyticsOpen(!analyticsOpen);
                  }
                }}
                style={{ textDecoration: "none", position: 'relative' }}
                title={isCollapsed ? 'Analytics' : ''}
              >
                <FontAwesomeIcon icon={faChartPie} className='fa-icon' style={{ minWidth: '20px' }} />
                {!isCollapsed && <span style={{ marginLeft: '8px' }}>Analytics</span>}
                {!isCollapsed && (
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    className={`fa-icon ${analyticsOpen ? 'rotate-chevron' : ''}`}
                    style={{ float: 'right', fontSize: '12px' }} 
                  />
                )}
                {isCollapsed && hoveredDropdown !== 'analytics' && <span className="nav-tooltip">Analytics</span>}
              </a>
              {/* Flyout menu for collapsed state */}
              {isCollapsed && hoveredDropdown === 'analytics' && (
                <div
                  className="collapsed-dropdown-menu"
                  onMouseEnter={() => {
                    if (hoverTimeoutRef.current) {
                      clearTimeout(hoverTimeoutRef.current);
                    }
                    setHoveredDropdown('analytics');
                  }}
                  onMouseLeave={() => {
                    if (hoverTimeoutRef.current) {
                      clearTimeout(hoverTimeoutRef.current);
                    }
                    hoverTimeoutRef.current = setTimeout(() => {
                      setHoveredDropdown(null);
                    }, 200);
                  }}
                  style={{
                    position: 'absolute',
                    left: '100%',
                    top: 0,
                    backgroundColor: '#ffffff',
                    border: '1px solid #e1e5e9',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    minWidth: '220px',
                    zIndex: 10000,
                    marginLeft: '10px',
                    padding: '4px 0'
                  }}>
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNavigate('/feature-limits', 'feature-limits');
                      setHoveredDropdown(null);
                    }}
                    style={{
                      padding: '12px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#333',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: getActiveState('/feature-limits') ? '#f0f4ff' : 'transparent',
                      borderLeft: getActiveState('/feature-limits') ? '3px solid #2e3192' : '3px solid transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!getActiveState('/feature-limits')) {
                        e.currentTarget.style.backgroundColor = '#f0f4ff';
                        e.currentTarget.style.borderLeft = '3px solid #2e3192';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!getActiveState('/feature-limits')) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderLeft = '3px solid transparent';
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                    <span>Usage Limit</span>
                  </div>
                  {loginType === 'Page' && (
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleNavigate('/viewpostsperformance', 'viewpostsperformance');
                        setHoveredDropdown(null);
                      }}
                      style={{
                        padding: '12px 20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#333',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        backgroundColor: getActiveState('/viewpostsperformance') ? '#f0f4ff' : 'transparent',
                        borderLeft: getActiveState('/viewpostsperformance') ? '3px solid #2e3192' : '3px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (!getActiveState('/viewpostsperformance')) {
                          e.currentTarget.style.backgroundColor = '#f0f4ff';
                          e.currentTarget.style.borderLeft = '3px solid #2e3192';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!getActiveState('/viewpostsperformance')) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderLeft = '3px solid transparent';
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faLineChart} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                      <span>Reports</span>
                    </div>
                  )}
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNavigate('/viewallpostedposts', 'viewallpostedposts');
                      setHoveredDropdown(null);
                    }}
                    style={{
                      padding: '12px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#333',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: getActiveState('/viewallpostedposts') ? '#f0f4ff' : 'transparent',
                      borderLeft: getActiveState('/viewallpostedposts') ? '3px solid #2e3192' : '3px solid transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!getActiveState('/viewallpostedposts')) {
                        e.currentTarget.style.backgroundColor = '#f0f4ff';
                        e.currentTarget.style.borderLeft = '3px solid #2e3192';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!getActiveState('/viewallpostedposts')) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderLeft = '3px solid transparent';
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faNewspaper} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                    <span>Published Posts</span>
                  </div>
                </div>
              )}
              {analyticsOpen && !isCollapsed && (
                <div style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  margin: '8px 12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden'
                }}>
                  <div>
                    <div
                      onClick={() => {
                        handleNavigate('/feature-limits', 'feature-limits');
                        setAnalyticsOpen(false);
                      }}
                      style={{
                        padding: '12px 20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#333',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        backgroundColor: getActiveState('/feature-limits') ? '#f0f4ff' : 'transparent',
                        borderLeft: getActiveState('/feature-limits') ? '3px solid #2e3192' : '3px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (!getActiveState('/feature-limits')) {
                          e.currentTarget.style.backgroundColor = '#f0f4ff';
                          e.currentTarget.style.borderLeft = '3px solid #2e3192';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!getActiveState('/feature-limits')) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderLeft = '3px solid transparent';
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                      <span>Usage Limit</span>
                    </div>
                    {/* Reports - Only for Page accounts (business branding) */}
                    {loginType === 'Page' && (
                      <div
                        onClick={() => {
                          handleNavigate('/viewpostsperformance', 'viewpostsperformance');
                          setAnalyticsOpen(false);
                        }}
                        style={{
                          padding: '12px 20px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#333',
                          fontSize: '14px',
                          transition: 'all 0.2s ease',
                          backgroundColor: getActiveState('/viewpostsperformance') ? '#f0f4ff' : 'transparent',
                          borderLeft: getActiveState('/viewpostsperformance') ? '3px solid #2e3192' : '3px solid transparent'
                        }}
                        onMouseEnter={(e) => {
                          if (!getActiveState('/viewpostsperformance')) {
                            e.currentTarget.style.backgroundColor = '#f0f4ff';
                            e.currentTarget.style.borderLeft = '3px solid #2e3192';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!getActiveState('/viewpostsperformance')) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.borderLeft = '3px solid transparent';
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faLineChart} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                        <span>Reports</span>
                      </div>
                    )}
                    {/* Published Posts */}
                    <div
                      onClick={() => {
                        handleNavigate('/viewallpostedposts', 'viewallpostedposts');
                        setAnalyticsOpen(false);
                      }}
                      style={{
                        padding: '12px 20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        color: '#333',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        backgroundColor: getActiveState('/viewallpostedposts') ? '#f0f4ff' : 'transparent',
                        borderLeft: getActiveState('/viewallpostedposts') ? '3px solid #2e3192' : '3px solid transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (!getActiveState('/viewallpostedposts')) {
                          e.currentTarget.style.backgroundColor = '#f0f4ff';
                          e.currentTarget.style.borderLeft = '3px solid #2e3192';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!getActiveState('/viewallpostedposts')) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderLeft = '3px solid transparent';
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faNewspaper} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                      <span>Published Posts</span>
                    </div>
                  </div>
                </div>
              )}
            </li>

            {/* Support Hub Dropdown */}




            {RoleID == 2 && (<li className={`dropdown ${selectedItem === 'ai' ? 'active' : ''}`}>
              <a
                href="#"
                onClick={() => handleNavigate('/userslist', 'userslist')}
                style={{ textDecoration: "none", position: 'relative' }}
                title={isCollapsed ? "User's List" : ''}
              >
                <FontAwesomeIcon icon={faUsersCog} className='fa-icon' style={{ minWidth: '20px' }} />
                {!isCollapsed && <span style={{ marginLeft: '8px' }}>User's List</span>}
                {isCollapsed && <span className="nav-tooltip">User's List</span>}
              </a>
            </li>)}

            {RoleID == 4 && (<li className={`dropdown ${selectedItem === 'viewpostsperformance' ? 'active' : ''}`}>
              <a
                href="#"
                onClick={() => handleNavigate('/viewpostsperformance', 'viewpostsperformance')}
                style={{ textDecoration: "none", position: 'relative' }}
                title={isCollapsed ? 'Performance' : ''}
              >
                <FontAwesomeIcon icon={faLineChart} className='fa-icon' style={{ minWidth: '20px' }} />
                {!isCollapsed && <span style={{ marginLeft: '8px' }}>Performance</span>}
                {isCollapsed && <span className="nav-tooltip">Performance</span>}
              </a>
            </li>)}
            {RoleID == 1 && (
              <li className={`dropdown ${selectedItem === 'integration' ? 'active' : ''}`}>
                <a
                  href="#"
                  onClick={() => handleNavigate('/integrations', 'integration')}
                  style={{ textDecoration: "none", position: 'relative' }}
                  title={isCollapsed ? 'Integrations' : ''}
                >
                  <FontAwesomeIcon icon={faLink} className='fa-icon' style={{ minWidth: '20px' }} />
                  {!isCollapsed && <span style={{ marginLeft: '8px' }}>Integrations</span>}
                  {isCollapsed && <span className="nav-tooltip">Integrations</span>}
                </a>
              </li>
            )}

            {/* Platform Settings - Always available */}
            <li className={`dropdown ${getActiveState('/platformsettings') ? 'active' : ''}`}>
              <a
                href="#"
                onClick={() => handleNavigate('/platformsettings', 'platformsettings')}
                style={{ textDecoration: "none", position: 'relative' }}
                title={isCollapsed ? 'Platform Settings' : ''}
              >
                <FontAwesomeIcon icon={faCog} className='fa-icon' style={{ minWidth: '20px' }} />
                {!isCollapsed && <span style={{ marginLeft: '8px' }}>Platform Settings</span>}
                {isCollapsed && <span className="nav-tooltip">Platform Settings</span>}
              </a>
            </li>

          </>

          )}
          <li
            className={`dropdown ${supportAccountOpen ? 'active' : ''}`}
            onMouseEnter={() => {
              if (isCollapsed) {
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                }
                setHoveredDropdown('support');
              }
            }}
            onMouseLeave={(e) => {
              if (isCollapsed) {
                // Clear any existing timeout
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                }
                // Check if moving to the flyout menu
                const menu = e.currentTarget.querySelector('.collapsed-dropdown-menu');
                const relatedTarget = e.relatedTarget;
                // Only close if not moving to the flyout menu and relatedTarget is a valid Node
                if (!menu || !relatedTarget || !(relatedTarget instanceof Node) || !menu.contains(relatedTarget)) {
                  // Add a small delay to make it easier to move to the menu
                  hoverTimeoutRef.current = setTimeout(() => {
                    setHoveredDropdown(null);
                  }, 200);
                }
              }
            }}
            style={{ position: 'relative' }}
          >
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                // On mobile, always toggle dropdown
                if (window.innerWidth <= 768) {
                  setSupportAccountOpen(!supportAccountOpen);
                } else if (isCollapsed) {
                  // Don't navigate, let hover show the menu
                  return;
                } else {
                  setSupportAccountOpen(!supportAccountOpen);
                }
              }}
              style={{ textDecoration: "none", position: 'relative' }}
              title={isCollapsed ? 'Support Hub' : ''}
            >
              <FontAwesomeIcon icon={faLifeRing} className='fa-icon' style={{ minWidth: '20px' }} />
              {!isCollapsed && <span style={{ marginLeft: '8px' }}>Support Hub</span>}
              {!isCollapsed && (
                <FontAwesomeIcon 
                  icon={faChevronDown} 
                  className={`fa-icon ${supportAccountOpen ? 'rotate-chevron' : ''}`}
                  style={{ float: 'right', fontSize: '12px' }} 
                />
              )}
              {isCollapsed && hoveredDropdown !== 'support' && <span className="nav-tooltip">Support Hub</span>}
            </a>
            {/* Flyout menu for collapsed state */}
            {isCollapsed && hoveredDropdown === 'support' && (
              <div
                className="collapsed-dropdown-menu"
                onMouseEnter={() => {
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                  }
                  setHoveredDropdown('support');
                }}
                onMouseLeave={() => {
                  if (hoverTimeoutRef.current) {
                    clearTimeout(hoverTimeoutRef.current);
                  }
                  hoverTimeoutRef.current = setTimeout(() => {
                    setHoveredDropdown(null);
                  }, 200);
                }}
                style={{
                  position: 'absolute',
                  left: '100%',
                  top: 0,
                  backgroundColor: '#ffffff',
                  border: '1px solid #e1e5e9',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  minWidth: '220px',
                  zIndex: 10000,
                  marginLeft: '10px',
                  padding: '4px 0'
                }}>
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNavigate('/subscription', 'subscription');
                    setHoveredDropdown(null);
                  }}
                  style={{
                    padding: '12px 20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#333',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    backgroundColor: getActiveState('/subscription') ? '#f0f4ff' : 'transparent',
                    borderLeft: getActiveState('/subscription') ? '3px solid #2e3192' : '3px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!getActiveState('/subscription')) {
                      e.currentTarget.style.backgroundColor = '#f0f4ff';
                      e.currentTarget.style.borderLeft = '3px solid #2e3192';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!getActiveState('/subscription')) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderLeft = '3px solid transparent';
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                  <span>Subscriptions</span>
                </div>
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNavigate('/resources', 'resources');
                    setHoveredDropdown(null);
                  }}
                  style={{
                    padding: '12px 20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#333',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    backgroundColor: getActiveState('/resources') ? '#f0f4ff' : 'transparent',
                    borderLeft: getActiveState('/resources') ? '3px solid #2e3192' : '3px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!getActiveState('/resources')) {
                      e.currentTarget.style.backgroundColor = '#f0f4ff';
                      e.currentTarget.style.borderLeft = '3px solid #2e3192';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!getActiveState('/resources')) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderLeft = '3px solid transparent';
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faQuestionCircle} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                  <span>Resources (Help Videos)</span>
                </div>
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNavigate('/report-issue', 'report-issue');
                    setHoveredDropdown(null);
                  }}
                  style={{
                    padding: '12px 20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#333',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    backgroundColor: getActiveState('/report-issue') ? '#f0f4ff' : 'transparent',
                    borderLeft: getActiveState('/report-issue') ? '3px solid #2e3192' : '3px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!getActiveState('/report-issue')) {
                      e.currentTarget.style.backgroundColor = '#f0f4ff';
                      e.currentTarget.style.borderLeft = '3px solid #2e3192';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!getActiveState('/report-issue')) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderLeft = '3px solid transparent';
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                  <span>Report an Issue</span>
                </div>
              </div>
            )}
            {supportAccountOpen && !isCollapsed && (
              <div style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e1e5e9',
                borderRadius: '8px',
                margin: '8px 12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }}>
                <div>
                  <div
                    onClick={() => {
                      handleNavigate('/subscription', 'subscription');
                      setSupportAccountOpen(false);
                    }}
                    style={{
                      padding: '12px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#333',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: getActiveState('/subscription') ? '#f0f4ff' : 'transparent',
                      borderLeft: getActiveState('/subscription') ? '3px solid #2e3192' : '3px solid transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!getActiveState('/subscription')) {
                        e.currentTarget.style.backgroundColor = '#f0f4ff';
                        e.currentTarget.style.borderLeft = '3px solid #2e3192';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!getActiveState('/subscription')) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderLeft = '3px solid transparent';
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                    <span>Subscriptions</span>
                  </div>
                  <div
                    onClick={() => {
                      handleNavigate('/resources', 'resources');
                      setSupportAccountOpen(false);
                    }}
                    style={{
                      padding: '12px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#333',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: getActiveState('/resources') ? '#f0f4ff' : 'transparent',
                      borderLeft: getActiveState('/resources') ? '3px solid #2e3192' : '3px solid transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!getActiveState('/resources')) {
                        e.currentTarget.style.backgroundColor = '#f0f4ff';
                        e.currentTarget.style.borderLeft = '3px solid #2e3192';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!getActiveState('/resources')) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderLeft = '3px solid transparent';
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                    <span>Resources (Help Videos)</span>
                  </div>
                  <div
                    onClick={() => {
                      handleNavigate('/report-issue', 'report-issue');
                      setSupportAccountOpen(false);
                    }}
                    style={{
                      padding: '12px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#333',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      backgroundColor: getActiveState('/report-issue') ? '#f0f4ff' : 'transparent',
                      borderLeft: getActiveState('/report-issue') ? '3px solid #2e3192' : '3px solid transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!getActiveState('/report-issue')) {
                        e.currentTarget.style.backgroundColor = '#f0f4ff';
                        e.currentTarget.style.borderLeft = '3px solid #2e3192';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!getActiveState('/report-issue')) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderLeft = '3px solid transparent';
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '12px', color: '#2e3192', width: '16px' }} />
                    <span>Report an Issue</span>
                  </div>
                </div>
              </div>
            )}
          </li>
          <li className={`dropdown ${selectedItem === 'logout' ? 'active' : ''}`}>
            <a
              href="#"
              onClick={handleLogout}
              style={{ textDecoration: "none", position: 'relative' }}
              title={isCollapsed ? 'Logout' : ''}
            >
              <FontAwesomeIcon icon={faSignOutAlt} className='fa-icon' style={{ minWidth: '20px' }} />
              {!isCollapsed && <span style={{ marginLeft: '8px' }}>Logout</span>}
              {isCollapsed && <span className="nav-tooltip">Logout</span>}
            </a>
          </li>

        </ul>



      </nav>

      <div id="content">
        <nav className="navbar navbar-expand-lg" style={{ backgroundColor: '#2e3192', color: '#fff', padding: '12px 20px', minHeight: '80px', height: '80px', marginTop: '20px' }}>
          <div className="container-fluid d-flex align-items-center" style={{ width: '100%' }}>
            {headerRight && (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                {headerRight}
              </div>
            )}

            {!headerRight && RoleID == 2 && (
              <div className="d-flex align-items-center ms-auto admin-user-select-container">
                <img
                  src={userProfile && userProfile.length > 0 && userProfile[0]?.ProfileUrl ? userProfile[0].ProfileUrl : defaultProfile}
                  alt="Profile"
                  className="rounded-circle"
                  style={{ width: '55px', height: '55px', marginRight: '10px', border: '2px solid rgba(255,255,255,0.3)' }}
                />
                <div style={{ width: "250px", marginBottom: '10px', position: 'relative' }}>
                  {selectedUserName && <span style={{ marginLeft: '10px', fontWeight: "bold", color: '#fff' }}>{selectedUserName}</span>}
                  <br />
                  <Select
                    options={userOptions}
                    onChange={handleChange}
                    placeholder="Search user..."
                    value={selectedOption}
                    className='mt-1 admin-user-select'
                    classNamePrefix="admin-select"
                    styles={{
                      menu: (base) => ({ 
                        ...base, 
                        zIndex: 9999,
                        position: 'absolute',
                        marginTop: '4px'
                      }),
                      control: (base) => ({
                        ...base,
                        minHeight: '38px'
                      })
                    }}
                  />
                </div>
              </div>
            )}

            {!headerRight && RoleID != 2 && (
              <WelcomeBanner />
            )}
          </div>
        </nav>

        <div className="container">
          {children}
        </div>
      </div>
    </div>
  );
};

export default SideNavbar;
